import { useEffect, useState } from "react";
import { Package, Layers, ClipboardCheck, Inbox, RefreshCw, Plus } from "lucide-react";
import { AdminOrderListItem, OrderTab, ORDER_STATUS_MAP, TAB_LABELS } from "./types";
import { AdminOrderManagementService } from "../../services/api/admin-order.service";
import OrderListingTable from "./OrderListingTable";
import OrderPagination from "./OrderPagination";
import PrescriptionModal from "./PrescriptionModal";
import GeneratePrescription from "./GeneratePrescription";
import ApproveOrder from "./ApproveOrder";
import PrepareOrder from "./PrepareOrder";
import ViewOrderDetails from "./ViewOrderDetails";
import ReturnOrderDetails from "./ReturnOrderDetails";
import CreateNewOrder from "./CreateNewOrder/CreateNewOrder";
import CancelOrderModal from "./CancelOrderModal";

const TABS: OrderTab[] = ["pending", "ongoing", "completed", "unprocessed", "return"];

const TAB_ICONS: Record<OrderTab, React.ReactNode> = {
  pending: <Layers size={14} />,
  ongoing: <Package size={14} />,
  completed: <ClipboardCheck size={14} />,
  unprocessed: <Inbox size={14} />,
  return: <RefreshCw size={14} />,
};

const OrderManagement = () => {
  const [activeTab, setActiveTab] = useState<OrderTab>("pending");
  const [orders, setOrders] = useState<AdminOrderListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, totalPages: 0, totalCount: 0 });
  const [refreshCount, setRefreshCount] = useState(0);
  const [prescriptionModal, setPrescriptionModal] = useState<{ open: boolean; orderId: number; orderNumber: string }>({
    open: false, orderId: 0, orderNumber: "",
  });
  const [cancelModal, setCancelModal] = useState<{ open: boolean; orderId: number | null; orderNumber?: string }>({
    open: false, orderId: null, orderNumber: "",
  });
  const [subView, setSubView] = useState<"list" | "generate" | "approve" | "prepare" | "view" | "return_view" | "create_new">("list");
  const [selectedOrder, setSelectedOrder] = useState<AdminOrderListItem | null>(null);

  useEffect(() => {
    fetchOrders();
  }, [activeTab, pagination.page, refreshCount]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      let response;
      if (activeTab === "return") {
        response = await AdminOrderManagementService.getReturnOrderListing(
          pagination.page, pagination.pageSize
        );
      } else {
        response = await AdminOrderManagementService.getOrderListing(
          pagination.page, pagination.pageSize, ORDER_STATUS_MAP[activeTab]
        );
      }
      if (response && response.success) {
        setOrders(response.data.ordersList || []);
        setPagination((p) => ({ ...p, totalPages: response.data.pagination.totalPages, totalCount: response.data.pagination.totalCount }));
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab: OrderTab) => {
    setActiveTab(tab);
    setPagination((p) => ({ ...p, page: 1 }));
    setSubView("list");
    setSelectedOrder(null);
  };

  const handleAction = (order: AdminOrderListItem) => {
    setSelectedOrder(order);
    if (activeTab === "return") {
      setSubView("return_view");
    } else if (activeTab === "ongoing" || activeTab === "completed" || activeTab === "unprocessed") {
      setSubView("view");
    } else {
      if (order.orderType === "direct_with_prescription" && !order.hasPrescription) setSubView("generate");
      else if (order.orderType === "direct_with_prescription" && order.hasPrescription) setSubView("approve");
      else if (order.orderType === "prescription_based") setSubView("prepare");
    }
  };

  const handleBackToList = () => {
    setSubView("list");
    setSelectedOrder(null);
    fetchOrders();
  };

  // Sub-views
  if (subView === "generate" && selectedOrder) {
    return <GeneratePrescription order={selectedOrder} onBack={handleBackToList} />;
  }
  if (subView === "approve" && selectedOrder) {
    return <ApproveOrder order={selectedOrder} onBack={handleBackToList} />;
  }
  if (subView === "prepare" && selectedOrder) {
    return <PrepareOrder order={selectedOrder} onBack={handleBackToList} />;
  }
  if (subView === "view" && selectedOrder) {
    return <ViewOrderDetails order={selectedOrder} onBack={handleBackToList} />;
  }
  if (subView === "return_view" && selectedOrder) {
    return <ReturnOrderDetails orderId={selectedOrder.orderId || (selectedOrder as any).returnId} onBack={handleBackToList} role="admin" />;
  }
  if (subView === "create_new") {
    return <CreateNewOrder onBack={handleBackToList} />;
  }

  return (
    <div className="h-full bg-slate-50 rounded-xl flex flex-col overflow-hidden">
      {/* Header — compact */}
      <div className="px-5 pt-3 pb-0 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-800 flex items-center justify-center text-white">
              <Package size={16} />
            </div>
            <h1 className="text-base font-bold text-slate-800">Order Management</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setPagination((p) => ({ ...p, page: 1 }));
                setRefreshCount((prev) => prev + 1);
              }}
              className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 transition-colors border-none cursor-pointer flex items-center justify-center"
              title="Refresh List"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={() => setSubView("create_new")}
              className="px-3.5 py-1.5 rounded-lg text-xs font-bold bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-md transition-all border-none cursor-pointer flex items-center gap-1.5"
            >
              <Plus size={14} />
              Add New Order
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="flex">
          {TABS.map((tab) => (
            <button
              key={tab}
              className={`px-4 py-2 text-xs font-semibold relative whitespace-nowrap transition-colors border-none bg-transparent cursor-pointer ${activeTab === tab ? "text-emerald-500" : "text-slate-500 hover:text-slate-700"
                }`}
              onClick={() => handleTabChange(tab)}
            >
              <span className="inline-flex items-center gap-1.5">
                {TAB_ICONS[tab]}
                {TAB_LABELS[tab]}
              </span>
              {activeTab === tab && (
                <span className="ml-1.5 px-1.5 py-px rounded-full text-[0.6rem] font-bold bg-emerald-50 text-emerald-500">
                  {pagination.totalCount}
                </span>
              )}
              {activeTab === tab && (
                <span className="absolute bottom-0 left-0 right-0 h-[2.5px] bg-gradient-to-r from-emerald-500 to-emerald-500 rounded-t" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto p-4">
        <OrderListingTable
          orders={orders}
          loading={loading}
          activeTab={activeTab}
          tabLabel={TAB_LABELS[activeTab]}
          onAction={handleAction}
          onCancelOrder={(ord) =>
            setCancelModal({
              open: true,
              orderId: ord.orderId,
              orderNumber: ord.orderNumber,
            })
          }
          onPrescriptionClick={(orderId, orderNumber) =>
            setPrescriptionModal({ open: true, orderId, orderNumber })
          }
          currentPage={pagination.page}
          pageSize={pagination.pageSize}
        />
        <OrderPagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalCount={pagination.totalCount}
          onPageChange={(p) => setPagination((prev) => ({ ...prev, page: p }))}
        />
      </div>

      {/* Prescription Modal */}
      {prescriptionModal.open && (
        <PrescriptionModal
          orderId={prescriptionModal.orderId}
          orderNumber={prescriptionModal.orderNumber}
          onClose={() => setPrescriptionModal({ open: false, orderId: 0, orderNumber: "" })}
        />
      )}

      {/* Cancel Order Modal */}
      <CancelOrderModal
        isOpen={cancelModal.open}
        onClose={() => setCancelModal({ open: false, orderId: null })}
        orderId={cancelModal.orderId}
        orderNumber={cancelModal.orderNumber}
        onSuccess={fetchOrders}
      />
    </div>
  );
};

export default OrderManagement;
